import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Trash2, Plus, AlertTriangle } from 'lucide-react';
import { DocumentType, isPlatformDocumentType, getDeliveryTimes } from '@/models/DocumentType';
import { toast } from 'sonner';

interface PricingTableDeliveryFeesProps {
  documentTypes: DocumentType[];
  feesApportionParties: any[];
}

const PricingTableDeliveryFees: React.FC<PricingTableDeliveryFeesProps> = ({ 
  documentTypes,
  feesApportionParties 
}) => {
  const { control, watch, setValue } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'delivery_fees'
  });



  const getAvailableOptions = () => {
    // Get all used combinations from form values
    const usedCombinations = fields.map((_, index) => {
      const values = control._formValues.delivery_fees?.[index];
      return values ? `${values.document_type_id}-${values.delivery_time_id}` : '';
    }).filter(Boolean);
    
    // Get all document types that have delivery times
    const documentTypesWithDeliveryTimes = documentTypes.filter(dt => 
      isPlatformDocumentType(dt) && getDeliveryTimes(dt).length > 0
    );
    
    // Get all possible combinations
    const allCombinations = [];
    for (const docType of documentTypesWithDeliveryTimes) {
      const deliveryTimes = getDeliveryTimes(docType);
      
      for (const deliveryTime of deliveryTimes) {
        // Skip default delivery times - they should not have fees requested
        if (deliveryTime.is_default) continue;
        
        const combination = `${docType.id}-${deliveryTime.id}`;
        if (!usedCombinations.includes(combination)) {
          allCombinations.push({
            document_type: docType,
            delivery_time: deliveryTime,
            combination
          });
        }
      }
    }
    
    return allCombinations;
  };

  const canAddMoreDeliveryFees = () => {
    return getAvailableOptions().length > 0;
  };

  const addNewDeliveryFee = () => {
    // Find the first available combination
    const availableOptions = getAvailableOptions();
    if (availableOptions.length > 0) {
      const firstOption = availableOptions[0];
      
      append({
        document_type_id: firstOption.document_type.id,
        delivery_time_id: firstOption.delivery_time.id,
        fees: [{
          fee_id: Math.floor(Math.random() * 1000000), // Temporary ID
          name: `${firstOption.document_type.name} - ${firstOption.delivery_time.name}`,
          amount: 0,
          multiply_by_applicants: false,
          married_count_as_one: false,
          apportionment: feesApportionParties.map(party => ({
            apportionment_party_id: party.id,
            amount: 0
          }))
        }]
      });
    }
  };

  const getAvailableDocumentTypes = (currentIndex: number) => {
    const currentValues = control._formValues.delivery_fees?.[currentIndex];
    
    return documentTypes.filter(dt => {
      if (!isPlatformDocumentType(dt)) return false;
      
      const deliveryTimes = getDeliveryTimes(dt);
      if (deliveryTimes.length === 0) return false;
      
      // If this document type is currently selected, always allow it
      if (currentValues?.document_type_id === dt.id) return true;
      
      // Check if this document type has any available delivery times (excluding default ones)
      const hasAvailableDeliveryTimes = deliveryTimes.some(deliveryTime => {
        // Skip default delivery times
        if (deliveryTime.is_default) return false;
        
        const isUsedByOthers = fields.some((_, index) => {
          if (index === currentIndex) return false;
          const values = control._formValues.delivery_fees?.[index];
          return values?.document_type_id === dt.id && values?.delivery_time_id === deliveryTime.id;
        });
        return !isUsedByOthers;
      });
      
      return hasAvailableDeliveryTimes;
    });
  };

  const getAvailableDeliveryTimes = (documentTypeId: number, currentIndex: number) => {
    const documentType = documentTypes.find(dt => dt.id === documentTypeId);
    if (!documentType || !isPlatformDocumentType(documentType)) return [];
    
    const allDeliveryTimes = getDeliveryTimes(documentType);
    const currentValues = control._formValues.delivery_fees?.[currentIndex];
    
    // Filter out delivery times that are already used by other rows or are default
    return allDeliveryTimes.filter(deliveryTime => {
      // Skip default delivery times - they should not have fees requested
      if (deliveryTime.is_default) return false;
      
      // If this delivery time is currently selected, always allow it
      if (currentValues?.delivery_time_id === deliveryTime.id) return true;
      
      // Check if this combination is used by other rows
      const isUsedByOthers = fields.some((_, index) => {
        if (index === currentIndex) return false;
        const values = control._formValues.delivery_fees?.[index];
        return values?.document_type_id === documentTypeId && values?.delivery_time_id === deliveryTime.id;
      });
      
      return !isUsedByOthers;
    });
  };

  const updateApportionmentAmount = (deliveryIndex: number, partyId: number, value: string) => {
    const numericValue = parseFloat(value) || 0;
    const currentApportionments = watch(`delivery_fees.${deliveryIndex}.fees.0.apportionment`) || [];
    
    const existingIndex = currentApportionments.findIndex(a => a.apportionment_party_id === partyId);
    
    let newApportionment;
    if (existingIndex >= 0) {
      // Update existing apportionment
      newApportionment = currentApportionments.map((a, index) => 
        index === existingIndex ? { ...a, amount: numericValue } : a
      );
    } else {
      // Add new apportionment
      newApportionment = [...currentApportionments, { apportionment_party_id: partyId, amount: numericValue }];
    }
    
    setValue(`delivery_fees.${deliveryIndex}.fees.0.apportionment`, newApportionment);
    
    // Validate total
    setTimeout(() => {
      validateRowTotal(deliveryIndex);
    }, 100);
  };

  const validateRowTotal = (deliveryIndex: number) => {
    const apportionments = watch(`delivery_fees.${deliveryIndex}.fees.0.apportionment`) || [];
    const feeAmount = watch(`delivery_fees.${deliveryIndex}.fees.0.amount`) || 0;
    const total = apportionments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    
    if (Math.abs(total - feeAmount) > 0.01) {
      toast.warning(`Delivery fee apportionments total ($${total.toFixed(2)}) should equal the fee amount of $${feeAmount.toFixed(2)}`);
    }
  };

  const isApportionmentValid = (deliveryIndex: number) => {
    const apportionments = watch(`delivery_fees.${deliveryIndex}.fees.0.apportionment`) || [];
    const feeAmount = watch(`delivery_fees.${deliveryIndex}.fees.0.amount`) || 0;
    const total = apportionments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    
    return Math.abs(total - feeAmount) <= 0.01;
  };

  const getApportionmentAmount = (apportionments: any[] = [], partyId: number) => {
    const apportionment = apportionments.find(a => a.apportionment_party_id === partyId);
    return apportionment ? apportionment.amount || 0 : 0;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Delivery Fees</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addNewDeliveryFee}
          disabled={!canAddMoreDeliveryFees()}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Delivery Fee
        </Button>
      </div>

      {fields.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Document Type</TableHead>
              <TableHead>Delivery Time</TableHead>
              <TableHead>Fee ($)</TableHead>
              {feesApportionParties.map(party => (
                <TableHead key={party.id}>
                  {party.description || party.entity_name}
                </TableHead>
              ))}
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => {
              const currentValues = control._formValues.delivery_fees?.[index];
              const availableDocumentTypes = getAvailableDocumentTypes(index);
              const availableDeliveryTimes = getAvailableDeliveryTimes(currentValues?.document_type_id, index);
              const currentApportionments = watch(`delivery_fees.${index}.fees.0.apportionment`) || [];
              
              return (
                <TableRow key={field.id}>
                  <TableCell>
                    <FormField
                      control={control}
                      name={`delivery_fees.${index}.document_type_id`}
                      render={({ field: formField }) => (
                        <FormItem>
                          <FormControl>
                            <Select
                              value={formField.value?.toString()}
                              onValueChange={(value) => {
                                const newDocTypeId = parseInt(value);
                                formField.onChange(newDocTypeId);
                                
                                // Find first available delivery time for this document type
                                const newDocType = documentTypes.find(dt => dt.id === newDocTypeId);
                                if (newDocType && isPlatformDocumentType(newDocType)) {
                                  const deliveryTimes = getDeliveryTimes(newDocType);
                                  const availableDeliveryTime = deliveryTimes.find(dt => {
                                    // Skip default delivery times
                                    if (dt.is_default) return false;
                                    
                                    const isUsed = fields.some((_, i) => {
                                      if (i === index) return false;
                                      const values = control._formValues.delivery_fees?.[i];
                                      return values?.document_type_id === newDocTypeId && values?.delivery_time_id === dt.id;
                                    });
                                    return !isUsed;
                                  });
                                  
                                  // Update delivery time and fee name using proper form methods
                                  if (availableDeliveryTime) {
                                    setValue(`delivery_fees.${index}.delivery_time_id`, availableDeliveryTime.id);
                                    setValue(`delivery_fees.${index}.fees.0.name`, `${newDocType.name} - ${availableDeliveryTime.name}`);
                                  } else {
                                    setValue(`delivery_fees.${index}.delivery_time_id`, undefined);
                                  }
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select document type" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableDocumentTypes.map((docType) => (
                                  <SelectItem key={docType.id} value={docType.id.toString()}>
                                    {docType.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={control}
                      name={`delivery_fees.${index}.delivery_time_id`}
                      render={({ field: formField }) => (
                        <FormItem>
                          <FormControl>
                            <Select
                              value={formField.value?.toString()}
                              onValueChange={(value) => {
                                const newDeliveryTimeId = parseInt(value);
                                formField.onChange(newDeliveryTimeId);
                                
                                // Update fee name using proper form method
                                const docType = documentTypes.find(dt => dt.id === currentValues?.document_type_id);
                                const deliveryTime = availableDeliveryTimes.find(dt => dt.id === newDeliveryTimeId);
                                if (docType && deliveryTime) {
                                  setValue(`delivery_fees.${index}.fees.0.name`, `${docType.name} - ${deliveryTime.name}`);
                                }
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select delivery time" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableDeliveryTimes.map((deliveryTime) => (
                                  <SelectItem key={deliveryTime.id} value={deliveryTime.id.toString()}>
                                    {deliveryTime.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <FormField
                      control={control}
                      name={`delivery_fees.${index}.fees.0.amount`}
                      render={({ field: formField }) => {
                        const isValid = isApportionmentValid(index);
                        return (
                          <FormItem>
                            <FormControl>
                              <div className="flex items-center space-x-1">
                                <span className="text-gray-500">$</span>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="0.00"
                                  className={`${!isValid ? 'border-orange-500' : ''}`}
                                  value={formField.value ?? 0}
                                  onChange={(e) => {
                                    const newAmount = parseFloat(e.target.value) || 0;
                                    formField.onChange(newAmount);
                                    // Validate total after amount change
                                    setTimeout(() => validateRowTotal(index), 100);
                                  }}
                                />
                                {!isValid && (
                                  <div className="flex items-center" title="Apportionment total doesn't match fee amount">
                                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                                  </div>
                                )}
                              </div>
                            </FormControl>
                          </FormItem>
                        );
                      }}
                    />
                  </TableCell>
                  {feesApportionParties.map(party => (
                    <TableCell key={party.id}>
                      <div className="flex items-center space-x-1">
                        <span className="text-gray-500">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={getApportionmentAmount(currentApportionments, party.id)}
                          onChange={(e) => updateApportionmentAmount(index, party.id, e.target.value)}
                          className="w-20"
                        />
                      </div>
                    </TableCell>
                  ))}
                  <TableCell>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          No delivery fees configured. Click "Add Delivery Fee" to get started.
        </div>
      )}
    </div>
  );
};

export default PricingTableDeliveryFees; 